import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { batch_get_all } from "../../util/Apiendpoits";
import { getWithTokenAPI, deleteWithTokenAPI } from "../../util/Api";
import { FiEye, FiTrash2, FiX } from "react-icons/fi";
import DynamicTable from "../ReusableComponent/DynamicTable";
import { FiFileText, FiVideo, FiCalendar } from "react-icons/fi";
import { SafeToast } from "../Toast/ShowToast";

import { useNavigate } from "react-router-dom";
import TableSkeleton from "../ReusableComponent/TableSkeleton";
const TotalBatches = () => {
  const { token, userId } = useSelector((state) => state.auth);
  const navigate = useNavigate();

  // --- State for Table ---
  const [loading, setLoading] = useState(false);
  const [batches, setBatches] = useState([]);
  const [search, setSearch] = useState("");
  const [selectedFilters, setSelectedFilters] = useState({});
  const [selectedRows, setSelectedRows] = useState([]);
  const [selectedBatchToDelete, setSelectedBatchToDelete] = useState(null);
  const [showDeleteBatchModal, setShowDeleteBatchModal] = useState(false);
  const [isDeletingBatch, setIsDeletingBatch] = useState(false);

  const handleDeleteBatch = async () => {
    if (!selectedBatchToDelete?.testBatchName) return;

    try {
      setIsDeletingBatch(true);
      const batchName = selectedBatchToDelete.testBatchName;
      const instId = userId || "695f3f832929fa82230aaf33";

      const testsRes = await getWithTokenAPI(
        token,
        `/batch/get-all?Institution_id=${instId}&testBatchName=${encodeURIComponent(batchName)}&all=true`
      );

      const batchIdsToDelete = new Set();
      if (testsRes?.statusCode === 200 && Array.isArray(testsRes.data?.data)) {
        testsRes.data.data.forEach((b) => {
          if (b?._id) batchIdsToDelete.add(b._id);
        });
      }
      if (selectedBatchToDelete.id) batchIdsToDelete.add(selectedBatchToDelete.id);
      if (selectedBatchToDelete._id) batchIdsToDelete.add(selectedBatchToDelete._id);

      if (batchIdsToDelete.size === 0) {
        SafeToast.error("No tests found to delete for this batch");
        return;
      }

      const deletePromises = Array.from(batchIdsToDelete).map((id) =>
        deleteWithTokenAPI(token, `/batch/delete/${id}`)
      );
      await Promise.all(deletePromises);

      SafeToast.success("Test batch and associated tests deleted permanently");
      setShowDeleteBatchModal(false);
      setSelectedBatchToDelete(null);
      await fetchBatchList();
    } catch (err) {
      console.error("Error deleting batch:", err);
      SafeToast.error("Failed to delete test batch");
    } finally {
      setIsDeletingBatch(false);
    }
  };

  // --- Fetch Data ---
  const fetchBatchList = async () => {
    try {
      setLoading(true);
      let queryUrl = `${batch_get_all}${userId}`;
      if (search) {
        queryUrl += `&search=${search}`;
      }
      const res = await getWithTokenAPI(token, queryUrl);

      if (res?.statusCode === 200) {
        const rawData = res.data.data || [];
        const groupedMap = {};
        
        rawData.forEach((item) => {
          const name = item.testBatchName;
          if (!groupedMap[name]) {
            groupedMap[name] = {
              ...item,
              id: item._id,
              totalTests: item.numberofTests || 1
            };
          }
        });

        const formattedData = Object.values(groupedMap);
        setBatches(formattedData);
      } else {
        setBatches([]);
      }
    } catch (error) {
      console.error("Batch list API error:", error);
      setBatches([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      fetchBatchList();
    }, 500); // Wait for 500ms before calling the API

    return () => clearTimeout(delayDebounceFn);
  }, [search]);

  const handleBatchClick = (row) => {
    navigate(`/batches/details/${row.id}`, { state: { batch: row } });
  };

  const getColumns = () => [
    {
      key: "batchName",
      label: "Batch Name",
      render: (row) => (
        <span
          style={{
            fontWeight: 700,
            color: "#0b5ed7",
            cursor: "pointer",
            display: "inline-flex",
            alignItems: "center",
            gap: "6px"
          }}
          onClick={(e) => {
            e.stopPropagation();
            handleBatchClick(row);
          }}
        >
          {row.testBatchName}
        </span>
      ),
    },

    {
      key: "examType",
      label: "Exam Type",
      render: (row) => row.examType?.value || "N/A",
    },

    {
      key: "totalTests",
      label: "Total Tests",
      render: (row) => (
        <span
          style={{
            display: "inline-block",
            padding: "2px 10px",
            borderRadius: "12px",
            background: "#eff6ff",
            color: "#1d4ed8",
            fontWeight: 600,
            fontSize: "13px"
          }}
        >
          {row.totalTests || 0}
        </span>
      ),
    },

    {
      key: "fees",
      label: "Batch Fees",
      render: (row) => (
        <strong style={{ color: "#059669" }}>
          ₹{row.testFees || 0}
        </strong>
      ),
    },

    {
      key: "actions",
      label: "Actions",
      render: (row) => (
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <button
            className="view-btn-action"
            style={{
              fontSize: "13px",
              padding: "6px 14px",
              gap: "6px",
              display: "inline-flex",
              alignItems: "center"
            }}
            onClick={(e) => {
              e.stopPropagation();
              handleBatchClick(row);
            }}
          >
            <FiEye size={15} />
            View Details
          </button>
          <button
            title="Delete Test Batch"
            style={{
              fontSize: "13px",
              padding: "6px 10px",
              display: "inline-flex",
              alignItems: "center",
              gap: "4px",
              background: "#fef2f2",
              border: "1px solid #fecaca",
              color: "#dc2626",
              borderRadius: "6px",
              cursor: "pointer",
              fontWeight: 500,
            }}
            onClick={(e) => {
              e.stopPropagation();
              setSelectedBatchToDelete(row);
              setShowDeleteBatchModal(true);
            }}
          >
            <FiTrash2 size={14} />
            Delete
          </button>
        </div>
      ),
    },
  ];

  // --- Filter Definitions ---
  const filters = [];

  const handleFilterChange = (key, option) => {
    setSelectedFilters((prev) => ({
      ...prev,
      [key]: option,
    }));
  };

  return (
    <div className="batch-list-page" style={{ padding: "20px" }}>
      <div style={{ marginBottom: "20px" }}>
        <h2>Total Batches</h2>
      </div>

      {loading ? (
        <TableSkeleton columns={getColumns()} rows={5} hasActions={true} />
      ) : (
        <DynamicTable
          columns={getColumns()}
          data={batches}
          filters={filters}
          search={search}
          setSearch={setSearch}
          selectedFilters={selectedFilters}
          handleFilterChange={handleFilterChange}
          selectedRows={selectedRows}
          setSelectedRows={setSelectedRows}
          onSearchClick={fetchBatchList}
          onRowClick={handleBatchClick}
          activeTab="All"
        />
      )}

      {/* Delete Test Batch Confirmation Dialog */}
      {showDeleteBatchModal && (
        <div
          className="modal-overlay"
          onClick={(e) => {
            if (e.target === e.currentTarget && !isDeletingBatch) {
              setShowDeleteBatchModal(false);
            }
          }}
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "rgba(15, 23, 42, 0.5)",
            backdropFilter: "blur(4px)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 99999,
            padding: "20px",
          }}
        >
          <div
            style={{
              background: "#ffffff",
              borderRadius: "16px",
              width: "100%",
              maxWidth: "500px",
              boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
              overflow: "hidden",
              border: "1px solid #f1f5f9",
            }}
          >
            {/* Modal Header */}
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                padding: "20px 24px 16px",
                borderBottom: "1px solid #f1f5f9",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                <div
                  style={{
                    width: "36px",
                    height: "36px",
                    borderRadius: "50%",
                    background: "#fee2e2",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "#dc2626",
                  }}
                >
                  <FiTrash2 size={20} />
                </div>
                <h3 style={{ margin: 0, fontSize: "18px", fontWeight: "600", color: "#0f172a" }}>
                  Delete Test Batch
                </h3>
              </div>
              <button
                type="button"
                onClick={() => !isDeletingBatch && setShowDeleteBatchModal(false)}
                disabled={isDeletingBatch}
                style={{
                  background: "transparent",
                  border: "none",
                  cursor: isDeletingBatch ? "not-allowed" : "pointer",
                  color: "#94a3b8",
                  padding: "4px",
                  borderRadius: "6px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <FiX size={20} />
              </button>
            </div>

            {/* Modal Body */}
            <div style={{ padding: "20px 24px" }}>
              <p
                style={{
                  margin: "0 0 12px",
                  fontSize: "15px",
                  fontWeight: "600",
                  color: "#1e293b",
                  lineHeight: "1.5",
                }}
              >
                Are you sure you want to delete this test batch and its associated tests permanently?
              </p>
              <div
                style={{
                  padding: "12px 14px",
                  background: "#fff1f2",
                  borderRadius: "8px",
                  border: "1px solid #ffe4e6",
                  fontSize: "13px",
                  color: "#be123c",
                  lineHeight: "1.5",
                }}
              >
                Batch: <strong>{selectedBatchToDelete?.testBatchName}</strong>
                <br />
                This action cannot be undone. All tests, question papers, answer keys, and schedules in this batch will be permanently removed.
              </div>
            </div>

            {/* Modal Footer */}
            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                gap: "12px",
                padding: "16px 24px 20px",
                borderTop: "1px solid #f1f5f9",
                background: "#f8fafc",
              }}
            >
              <button
                type="button"
                disabled={isDeletingBatch}
                onClick={() => setShowDeleteBatchModal(false)}
                style={{
                  padding: "10px 18px",
                  borderRadius: "8px",
                  border: "1px solid #cbd5e1",
                  background: "#ffffff",
                  color: "#475569",
                  fontWeight: "500",
                  fontSize: "14px",
                  cursor: isDeletingBatch ? "not-allowed" : "pointer",
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={isDeletingBatch}
                onClick={handleDeleteBatch}
                style={{
                  padding: "10px 20px",
                  borderRadius: "8px",
                  border: "none",
                  background: isDeletingBatch ? "#fca5a5" : "#dc2626",
                  color: "#ffffff",
                  fontWeight: "600",
                  fontSize: "14px",
                  cursor: isDeletingBatch ? "not-allowed" : "pointer",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "8px",
                }}
              >
                {isDeletingBatch ? "Deleting..." : "Delete Permanently"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TotalBatches;